import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Send, Home, Zap, ClipboardList, Building2, Image as ImageIcon, X, Copy, Check, Menu, Plus, Edit2, Trash2, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { CurrentPage } from '../App';
import type { User } from '@supabase/supabase-js';
import type { WorkflowMode, ChatMessage as AIChatMessage, RollingSummary, PropertyRecord } from '../types/aiChat';
import { sendChatRequest, initializePropertyRecord, resizeImage } from '../services/openAIService';
import { getChatSessions, createChatSession, getChatSession, updateChatSession, renameChatSession, deleteChatSession, type AIChatSession } from '../services/aiChatService';
import { getAIUsage, incrementAIUsage, hasExceededLimit, type AIUsageData } from '../services/aiUsageService';

// AI Usage Bubble Styles
const aiUsageBubbleStyles = `
  :root{
    --ai-card-bg: rgba(255,255,255,0.95);
    --ai-card-border: rgba(15, 23, 42, 0.08);
    --ai-text: rgba(15, 23, 42, 0.92);
    --ai-muted: rgba(15, 23, 42, 0.58);
    --ai-shadow: 0 8px 20px rgba(2, 6, 23, 0.08);
    --ai-radius: 12px;
    --ai-blur: 8px;
    --ai-accent: #2f7bf6;
    --ai-track: rgba(15, 23, 42, 0.10);
  }

  .ai-usage-bubble{
    width: 180px;
    padding: 8px 10px 6px;
    border-radius: var(--ai-radius);
    background: var(--ai-card-bg);
    border: 1px solid var(--ai-card-border);
    box-shadow: var(--ai-shadow);
    backdrop-filter: blur(var(--ai-blur));
    -webkit-backdrop-filter: blur(var(--ai-blur));
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Apple Color Emoji","Segoe UI Emoji";
    color: var(--ai-text);
  }

  .ai-usage-row{
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px;
    margin-bottom: 6px;
  }

  .ai-usage-title{
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--ai-muted);
  }

  .ai-usage-count{
    display: flex;
    align-items: baseline;
    gap: 4px;
    font-weight: 700;
    color: var(--ai-text);
  }

  .ai-usage-used{
    font-size: 15px;
    line-height: 1;
  }

  .ai-usage-sep,
  .ai-usage-total{
    font-size: 11px;
    color: var(--ai-muted);
    font-weight: 600;
  }

  .ai-usage-bar{
    height: 6px;
    border-radius: 999px;
    background: var(--ai-track);
    overflow: hidden;
    position: relative;
  }

  .ai-usage-bar-fill{
    height: 100%;
    width: 0%;
    border-radius: 999px;
    background: var(--ai-accent);
    position: relative;
  }

  .ai-usage-bar-fill::after{
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      rgba(255,255,255,0.0),
      rgba(255,255,255,0.35),
      rgba(255,255,255,0.0)
    );
    transform: translateX(-60%);
    animation: aiSheen 2.6s ease-in-out infinite;
    opacity: 0.55;
  }

  @keyframes aiSheen{
    0%   { transform: translateX(-60%); }
    55%  { transform: translateX(140%); }
    100% { transform: translateX(140%); }
  }

  .ai-usage-footer{
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
    gap: 6px;
  }

  .ai-usage-reset{
    font-size: 9px;
    color: var(--ai-muted);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ai-usage-dot{
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: var(--ai-accent);
    box-shadow: 0 0 0 3px rgba(47, 123, 246, 0.16);
    flex: 0 0 auto;
  }

  @media (max-width: 360px){
    .ai-usage-bubble{ width: 160px; }
  }
`;

// Display message format (UI layer)
interface DisplayMessage {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  images?: string[]; // Base64 images attached to the message
  timestamp: string;
  workflowMode?: WorkflowMode;
}

// Helper to convert between API and display formats
const toChatMessage = (msg: DisplayMessage): AIChatMessage => ({
  id: msg.id,
  role: msg.sender === 'user' ? 'user' : 'assistant',
  content: msg.text,
  timestamp: msg.timestamp,
  workflowMode: msg.workflowMode || 'general'
});

const toDisplayMessage = (msg: AIChatMessage): DisplayMessage => ({
  id: msg.id,
  text: msg.content,
  sender: msg.role === 'user' ? 'user' : 'ai',
  timestamp: msg.timestamp,
  workflowMode: msg.workflowMode
});

interface ChatPageProps {
  onNavigate: (page: CurrentPage) => void;
  currentUser: User | null;
}

const ChatPage: React.FC<ChatPageProps> = ({ onNavigate, currentUser }) => {
  // Scroll to top when page loads (backup - App.tsx also handles this)
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get user's first name for personalized greeting
  const firstName = currentUser?.user_metadata?.first_name || 'there';

  // Workflow mode state
  const [selectedMode, setSelectedMode] = useState<WorkflowMode | null>(null);
  const [propertyRecord, setPropertyRecord] = useState<PropertyRecord | null>(null);
  const [rollingSummary, setRollingSummary] = useState<RollingSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [viewingImage, setViewingImage] = useState<string | null>(null);
  
  // Session management
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [chatSessions, setChatSessions] = useState<AIChatSession[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  
  // Usage tracking
  const [usageData, setUsageData] = useState<AIUsageData | null>(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  // Load chat sessions and usage on mount
  useEffect(() => {
    loadChatSessions();
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    setIsLoadingUsage(true);
    const usage = await getAIUsage();
    setUsageData(usage);
    setIsLoadingUsage(false);
  };

  // Auto-save chat session when messages/context changes
  useEffect(() => {
    if (currentSessionId && messages.length > 0) {
      const timeoutId = setTimeout(() => {
        const apiMessages = messages.map(toChatMessage);
        updateChatSession(currentSessionId, apiMessages, rollingSummary, propertyRecord);
      }, 1000); // Debounce saves by 1 second
      return () => clearTimeout(timeoutId);
    }
  }, [messages, rollingSummary, propertyRecord, currentSessionId]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [inputText]);

  const loadChatSessions = async () => {
    const sessions = await getChatSessions();
    setChatSessions(sessions);
  };

  const handleNewChat = () => {
    setCurrentSessionId(null);
    setSelectedMode(null);
    setMessages([]);
    setPropertyRecord(null);
    setRollingSummary(null);
    setInputText('');
    setUploadedImages([]);
    setIsSidebarOpen(false);
  };

  const handleLoadSession = async (sessionId: string) => {
    const session = await getChatSession(sessionId);
    if (session) {
      setCurrentSessionId(session.id);
      setSelectedMode(session.workflow_mode);
      setMessages(session.messages.map(toDisplayMessage));
      setRollingSummary(session.rolling_summary);
      setPropertyRecord(session.property_record);
      setIsSidebarOpen(false);
    }
  };

  const handleRenameSession = async (sessionId: string) => {
    if (editingTitle.trim()) {
      await renameChatSession(sessionId, editingTitle.trim());
      await loadChatSessions();
      setEditingSessionId(null);
      setEditingTitle('');
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (window.confirm('Delete this chat? This cannot be undone.')) {
      await deleteChatSession(sessionId);
      if (currentSessionId === sessionId) {
        handleNewChat();
      }
      await loadChatSessions();
    }
  };

  // Initialize workflow mode
  const handleSelectMode = async (mode: WorkflowMode) => {
    setSelectedMode(mode);
    const record = initializePropertyRecord(mode);
    const summary = {
      mode,
      lastUpdated: new Date().toISOString(),
      summary: '',
      keyPoints: []
    };
    setPropertyRecord(record);
    setRollingSummary(summary);

    // Create new session in database
    if (!currentSessionId) {
      const modeLabels = { epc: 'EPC Assessment', inventory: 'Property Inventory', general: 'Property Chat' };
      const session = await createChatSession(mode, `New ${modeLabels[mode]}`, summary, record);
      if (session) {
        setCurrentSessionId(session.id);
        await loadChatSessions();
      }
    }
  };

  // Send message to OpenAI
  const handleSendMessage = async () => {
    if ((!inputText.trim() && uploadedImages.length === 0) || !selectedMode || isLoading) return;

    // Check usage limit
    const limitExceeded = await hasExceededLimit();
    if (limitExceeded) {
      alert(`You've reached your monthly AI question limit. Upgrade to Pro for more questions!`);
      return;
    }

    const userMessage: DisplayMessage = {
      id: Date.now().toString(),
      text: inputText || '📷 Sent images',
      sender: 'user',
      images: uploadedImages.length > 0 ? uploadedImages : undefined,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      workflowMode: selectedMode
    };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    
    const imagesToSend = [...uploadedImages];
    const questionText = inputText.trim();
    const isFirstQuestion = messages.length === 0; // Check BEFORE adding user message
    setInputText('');
    setUploadedImages([]);
    setIsLoading(true);

    try {
      // Get recent context (last 2 messages)
      const recentContext = messages.slice(-2).map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      })) as any[];

      // Send to OpenAI with images
      const response = await sendChatRequest({
        mode: selectedMode,
        userMessage: inputText,
        rollingSummary: rollingSummary?.summary || '',
        propertyRecordSubset: propertyRecord ? propertyRecord : { mode: selectedMode } as any,
        recentContext,
        images: imagesToSend.length > 0 ? imagesToSend : undefined
      });

      // Stream AI response with typing effect
      const fullText = response.messageContent;
      setIsStreaming(true);
      setStreamingText('');
      
      // Add placeholder message that will be updated
      const aiMessageId = String(Date.now() + 1);
      const aiResponse: DisplayMessage = {
        id: aiMessageId,
        text: '',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        workflowMode: selectedMode
      };
      setMessages(prevMessages => [...prevMessages, aiResponse]);

      // Stream text character by character
      let currentIndex = 0;
      const streamInterval = setInterval(() => {
        if (currentIndex < fullText.length) {
          const chunkSize = Math.min(3, fullText.length - currentIndex); // 3 chars at a time
          currentIndex += chunkSize;
          const textSoFar = fullText.substring(0, currentIndex);
          setStreamingText(textSoFar);
          
          // Update the last message
          setMessages(prevMessages => 
            prevMessages.map(msg => 
              msg.id === aiMessageId ? { ...msg, text: textSoFar } : msg
            )
          );
        } else {
          clearInterval(streamInterval);
          setIsStreaming(false);
          setStreamingText('');
        }
      }, 15); // 15ms per chunk for smooth streaming

      // Update rolling summary if provided
      if (response.updatedSummary) {
        setRollingSummary(prev => prev ? {
          ...prev,
          summary: response.updatedSummary!,
          lastUpdated: new Date().toISOString()
        } : null);
      }

      // Increment usage counter
      await incrementAIUsage();
      await loadUsageData();

      // Auto-title chat from first question (after successful response)
      if (isFirstQuestion && currentSessionId && questionText) {
        // Convert to title case (capitalize each word) and limit to 30 chars
        const titleCased = questionText
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        const autoTitle = titleCased.slice(0, 20) + (titleCased.length > 30 ? '...' : '');
        await renameChatSession(currentSessionId, autoTitle);
        await loadChatSessions();
      }

      // Update property record if provided
      if (response.recordUpdates) {
        setPropertyRecord(prev => {
          if (!prev) return null;
          // Type-safe merge based on mode
          if (prev.mode === 'epc' && response.recordUpdates?.mode === 'epc') {
            return { ...prev, ...response.recordUpdates };
          } else if (prev.mode === 'inventory' && response.recordUpdates?.mode === 'inventory') {
            return { ...prev, ...response.recordUpdates };
          } else if (prev.mode === 'general' && response.recordUpdates?.mode === 'general') {
            return { ...prev, ...response.recordUpdates };
          }
          return prev;
        });
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setIsStreaming(false);
      const errorMessage: DisplayMessage = {
        id: String(Date.now() + 1),
        text: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        workflowMode: selectedMode
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    // Check limit
    if (uploadedImages.length + files.length > 5) {
      alert('Maximum 5 images allowed per message');
      return;
    }

    const resizedImages: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        // Convert File to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        // Resize the base64 image
        const resized = await resizeImage(base64, 1024);
        resizedImages.push(resized);
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }

    setUploadedImages(prev => [...prev, ...resizedImages].slice(0, 5));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleImageClick = (imageUrl: string) => {
    setViewingImage(imageUrl);
  };

  const handleSaveImage = async (imageUrl: string) => {
    try {
      // Convert base64 to blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `noor-chat-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error saving image:', error);
      alert('Failed to save image');
    }
  };

  const handleCopyMessage = async (messageId: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Inject AI Usage Bubble Styles */}
      <style dangerouslySetInnerHTML={{ __html: aiUsageBubbleStyles }} />
      
    <div className="flex h-screen bg-white overflow-hidden">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-80 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`} style={{ paddingTop: 'calc(env(safe-area-inset-top) + 4rem)' }}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Chat History</h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close sidebar"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* New Chat Button */}
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={handleNewChat}
              className="w-full flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg"
            >
              <Plus size={20} />
              <span className="font-medium">New Chat</span>
            </button>
          </div>


          {/* Chat List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {chatSessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <MessageSquare className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">No chats yet</p>
                <p className="text-gray-400 text-xs mt-1">Start a new conversation!</p>
              </div>
            ) : (
              chatSessions.map((session) => (
                <div
                  key={session.id}
                  className={`group relative rounded-lg border transition-all ${
                    currentSessionId === session.id
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  {editingSessionId === session.id ? (
                    <div className="p-3">
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameSession(session.id);
                          if (e.key === 'Escape') {
                            setEditingSessionId(null);
                            setEditingTitle('');
                          }
                        }}
                        onBlur={() => handleRenameSession(session.id)}
                        autoFocus
                        className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleLoadSession(session.id)}
                        className="w-full text-left p-3 rounded-lg"
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-0.5">
                            {session.workflow_mode === 'epc' ? (
                              <Zap size={16} className="text-yellow-600" />
                            ) : session.workflow_mode === 'inventory' ? (
                              <ClipboardList size={16} className="text-blue-600" />
                            ) : (
                              <Building2 size={16} className="text-green-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{session.title}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {new Date(session.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Action buttons - Always visible */}
                      <div className="absolute right-2 top-2 flex gap-1 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSessionId(session.id);
                            setEditingTitle(session.title);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Rename"
                        >
                          <Edit2 size={14} className="text-gray-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSession(session.id);
                          }}
                          className="p-1.5 hover:bg-red-50 rounded transition-colors"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} className="text-red-600" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex flex-col flex-1 h-screen overflow-hidden">
      {/* Header - Fixed */}
      <header className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 py-4 sm:py-5 shadow-xl sticky top-0 z-50 flex-shrink-0 header-safe-area">
        <div className="container mx-auto px-4 flex justify-between items-center relative z-10">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-white/90 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
            title="Toggle sidebar"
          >
            <Menu size={22} />
          </button>
          
          <button
            onClick={() => onNavigate({ page: 'chatHub' })}
            className="text-white/90 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
            title="Back to Chat Hub"
          >
            <ArrowLeft size={22} />
          </button>
          
          <div className="flex items-center space-x-3 flex-1 justify-center">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-white">Noor AI</h1>
              <p className="text-yellow-100 text-xs sm:text-sm hidden sm:block">
                {selectedMode 
                  ? selectedMode === 'epc' ? 'Energy Assessment Support' : selectedMode === 'inventory' ? 'Property Inventory Support' : 'General Property Support'
                  : 'Property Assessment Chat'
                }
              </p>
            </div>
          </div>
          <div style={{ width: '38px' }} /> {/* Spacer */}
        </div>
      </header>

      {/* Main - Scrollable Content */}
      <main className="flex-1 overflow-y-auto px-[18px] pt-[22px] pb-[22px]" style={{ background: '#f6f7fb' }}>
        {!selectedMode ? (
          /* Mode Selection */
          <div className="min-h-[520px] flex items-center justify-center">
            <div className="w-full max-w-[420px]">
              <div className="text-center mb-8">
                <h2 className="text-[32px] font-extrabold text-gray-900 mb-3">Choose Your Workflow</h2>
                <p className="text-[18px] text-gray-600">Select the type of assistance you need</p>
              </div>

              <div className="space-y-4">
                {/* EPC/RdSAP Mode */}
                <button
                  onClick={() => handleSelectMode('epc')}
                  className="w-full bg-white border-2 border-gray-200 rounded-[20px] p-5 text-left hover:border-yellow-400 hover:shadow-lg transition-all duration-200 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Zap size={28} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[22px] font-bold text-gray-900 mb-1">EPC / RdSAP</h3>
                      <p className="text-[15px] text-gray-600 leading-relaxed">Energy assessment support with evidence-based analysis of building fabric, heating systems, and compliance.</p>
                    </div>
                  </div>
                </button>

                {/* Inventory Mode */}
                <button
                  onClick={() => handleSelectMode('inventory')}
                  className="w-full bg-white border-2 border-gray-200 rounded-[20px] p-5 text-left hover:border-blue-400 hover:shadow-lg transition-all duration-200 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <ClipboardList size={28} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[22px] font-bold text-gray-900 mb-1">Property Inventory</h3>
                      <p className="text-[15px] text-gray-600 leading-relaxed">Room-by-room condition reporting with professional, legally-safe inventory language.</p>
                    </div>
                  </div>
                </button>

                {/* General Property Mode */}
                <button
                  onClick={() => handleSelectMode('general')}
                  className="w-full bg-white border-2 border-gray-200 rounded-[20px] p-5 text-left hover:border-green-400 hover:shadow-lg transition-all duration-200 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Building2 size={28} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[22px] font-bold text-gray-900 mb-1">General Property</h3>
                      <p className="text-[15px] text-gray-600 leading-relaxed">Professional property data support for estate agents, architects, and surveyors.</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        ) : messages.length === 0 ? (
          /* Welcome Message After Mode Selection */
          <div className="flex items-start pt-8">
            <section className="max-w-[540px] w-full flex items-start gap-5">
              {/* Avatar */}
              <div className="w-[66px] h-[66px] rounded-full flex items-center justify-center flex-shrink-0 border border-gray-900/5" style={{
                background: '#f6d46b',
                boxShadow: '0 12px 28px rgba(17,24,39,.10)'
              }}>
                <svg width="44" height="44" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="34" r="22" fill="#0F172A" opacity=".10"/>
                  <rect x="14" y="18" width="36" height="32" rx="16" fill="#1F2937" opacity=".14"/>
                  <rect x="18" y="22" width="28" height="24" rx="12" fill="#0B1220"/>
                  <circle cx="26" cy="34" r="4" fill="#7DD3FC"/>
                  <circle cx="38" cy="34" r="4" fill="#7DD3FC"/>
                  <path d="M24 42c3.5 3 12.5 3 16 0" stroke="#7DD3FC" strokeWidth="3" strokeLinecap="round"/>
                  <path d="M32 12v6" stroke="#0B1220" strokeWidth="3" strokeLinecap="round"/>
                  <circle cx="32" cy="10" r="4" fill="#7DD3FC"/>
                </svg>
              </div>

              {/* Bubble with tail */}
              <div className="flex-1 relative bg-white/92 border rounded-[22px] px-[18px] py-4" style={{
                borderColor: 'rgba(17,24,39,.06)',
                boxShadow: '0 10px 30px rgba(17,24,39,.08)'
              }}>
                {/* Tail */}
                <div className="absolute -left-[8px] top-[22px] w-4 h-4 bg-white/92 border-l border-b rotate-45 rounded-bl" style={{
                  borderColor: 'rgba(17,24,39,.06)'
                }}></div>
                
                <p className="text-[26px] font-extrabold tracking-tight m-0 mb-2 text-gray-900">Noor AI</p>
                <p className="text-[20px] text-gray-900/60 tracking-tight m-0">
                  {selectedMode === 'epc' 
                    ? `Ready to help with your energy assessment, ${firstName}. Upload photos of boilers, cylinders, or ask about building fabric.`
                    : selectedMode === 'inventory'
                    ? `Ready to help document property condition, ${firstName}. Start with a room name or upload photos.`
                    : `Ready to help with property data, ${firstName}. Ask about measurements, materials, or upload photos.`
                  }
                </p>
              </div>
            </section>
          </div>
        ) : (
          <div className="space-y-6">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex items-start gap-[14px] ${
                  msg.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {msg.sender === 'ai' && (
                  <div className="w-[66px] h-[66px] rounded-full flex items-center justify-center flex-shrink-0 border border-gray-900/5" style={{
                    background: '#f6d46b',
                    boxShadow: '0 12px 28px rgba(17,24,39,.10)'
                  }}>
                    <svg width="44" height="44" viewBox="0 0 64 64" fill="none">
                      <circle cx="32" cy="34" r="22" fill="#0F172A" opacity=".10"/>
                      <rect x="14" y="18" width="36" height="32" rx="16" fill="#1F2937" opacity=".14"/>
                      <rect x="18" y="22" width="28" height="24" rx="12" fill="#0B1220"/>
                      <circle cx="26" cy="34" r="4" fill="#7DD3FC"/>
                      <circle cx="38" cy="34" r="4" fill="#7DD3FC"/>
                      <path d="M24 42c3.5 3 12.5 3 16 0" stroke="#7DD3FC" strokeWidth="3" strokeLinecap="round"/>
                      <path d="M32 12v6" stroke="#0B1220" strokeWidth="3" strokeLinecap="round"/>
                      <circle cx="32" cy="10" r="4" fill="#7DD3FC"/>
                    </svg>
                  </div>
                )}
                <div className="flex-1 max-w-full relative bg-white border rounded-[22px] px-[20px] py-5" style={{
                  borderColor: 'rgba(17,24,39,.06)',
                  boxShadow: '0 10px 30px rgba(17,24,39,.08)'
                }}>
                  {msg.sender === 'ai' && (
                    <div className="absolute -left-[8px] top-[22px] w-4 h-4 bg-white border-l border-b rotate-45 rounded-bl" style={{
                      borderColor: 'rgba(17,24,39,.06)'
                    }}></div>
                  )}
                  {msg.sender === 'ai' && (
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[26px] font-extrabold tracking-tight m-0 text-gray-900">Noor AI</p>
                      <button
                        onClick={() => handleCopyMessage(msg.id, msg.text)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                        aria-label="Copy message"
                      >
                        {copiedMessageId === msg.id ? (
                          <Check size={16} className="text-green-600" />
                        ) : (
                          <Copy size={16} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                  )}
                  {msg.sender === 'ai' ? (
                    <div className="prose prose-slate max-w-none">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({node, ...props}) => {
                            // Render paragraph with evidence badge styling
                            return <p className="text-[17px] leading-[1.6] text-gray-900/75 mb-4 last:mb-0" {...props} />;
                          },
                          strong: ({node, children, ...props}) => {
                            // Check if this bold text is an evidence badge
                            const text = String(children);
                            if (text === 'EVIDENCED:' || text === 'EVIDENCED') {
                              return (
                                <strong className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-50 text-green-700 font-bold text-[15px] border border-green-200" {...props}>
                                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                  EVIDENCED
                                </strong>
                              );
                            }
                            if (text === 'INFERRED:' || text === 'INFERRED') {
                              return (
                                <strong className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-bold text-[15px] border border-blue-200" {...props}>
                                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                  INFERRED
                                </strong>
                              );
                            }
                            if (text === 'ASSUMED:' || text === 'ASSUMED') {
                              return (
                                <strong className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-yellow-50 text-yellow-700 font-bold text-[15px] border border-yellow-200" {...props}>
                                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                  ASSUMED
                                </strong>
                              );
                            }
                            return <strong className="font-bold text-gray-900" {...props}>{children}</strong>;
                          },
                          em: ({node, ...props}) => <em className="italic text-gray-800" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc pl-6 space-y-2 text-gray-900/75 mb-4" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal pl-6 space-y-2 text-gray-900/75 mb-4" {...props} />,
                          li: ({node, ...props}) => <li className="text-[17px] leading-[1.6] text-gray-900/75" {...props} />,
                          h1: ({node, ...props}) => <h1 className="text-[24px] font-bold text-gray-900 mb-3 mt-4 first:mt-0" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-[22px] font-bold text-gray-900 mb-3 mt-4 first:mt-0" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-[20px] font-bold text-gray-900 mb-2 mt-3 first:mt-0" {...props} />,
                          code: ({node, inline, className, children, ...props}: any) => {
                            // Hide code blocks that contain property record JSON structures
                            const codeContent = String(children);
                            const isPropertyRecord = !inline && (
                              codeContent.includes('"mode":') || 
                              codeContent.includes('"dimensions":') ||
                              codeContent.includes('"materials":') ||
                              codeContent.includes('"features":') ||
                              codeContent.includes('"metadata":') ||
                              codeContent.includes('Property record update')
                            );
                            
                            if (isPropertyRecord) {
                              return null; // Hide property record JSON blocks
                            }
                            
                            return inline 
                              ? <code className="bg-gray-100 text-gray-900 px-1.5 py-0.5 rounded text-[15px] font-mono" {...props}>{children}</code>
                              : <code className="block bg-gray-100 text-gray-900 p-3 rounded-lg text-[15px] font-mono overflow-x-auto mb-4" {...props}>{children}</code>;
                          },
                          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-yellow-400 pl-4 italic text-gray-900/70 my-4" {...props} />,
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="relative">
                      <p className="text-[17px] leading-[1.6] text-gray-900/75 m-0">{msg.text}</p>
                      
                      {/* Display attached images */}
                      {msg.images && msg.images.length > 0 && (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {msg.images.map((img, index) => (
                            <div 
                              key={index} 
                              className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 cursor-pointer hover:border-yellow-400 transition-colors group"
                              onClick={() => handleImageClick(img)}
                            >
                              <img src={img} alt={`Attachment ${index + 1}`} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <ImageIcon size={20} className="text-white" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Copy button for user messages */}
                      <button
                        onClick={() => handleCopyMessage(msg.id, msg.text)}
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-lg flex items-center justify-center bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
                        aria-label="Copy message"
                      >
                        {copiedMessageId === msg.id ? (
                          <Check size={14} className="text-green-600" />
                        ) : (
                          <Copy size={14} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Thinking Indicator */}
            {isLoading && !isStreaming && (
              <div className="flex items-start gap-[14px]">
                <div className="w-[66px] h-[66px] rounded-full flex items-center justify-center flex-shrink-0 border border-gray-900/5" style={{
                  background: '#f6d46b',
                  boxShadow: '0 12px 28px rgba(17,24,39,.10)'
                }}>
                  <svg width="44" height="44" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="34" r="22" fill="#0F172A" opacity=".10"/>
                    <rect x="14" y="18" width="36" height="32" rx="16" fill="#1F2937" opacity=".14"/>
                    <rect x="18" y="22" width="28" height="24" rx="12" fill="#0B1220"/>
                    <circle cx="26" cy="34" r="4" fill="#7DD3FC"/>
                    <circle cx="38" cy="34" r="4" fill="#7DD3FC"/>
                    <path d="M24 42c3.5 3 12.5 3 16 0" stroke="#7DD3FC" strokeWidth="3" strokeLinecap="round"/>
                    <path d="M32 12v6" stroke="#0B1220" strokeWidth="3" strokeLinecap="round"/>
                    <circle cx="32" cy="10" r="4" fill="#7DD3FC"/>
                  </svg>
                </div>
                <div className="flex-1 max-w-full relative bg-white border rounded-[22px] px-[20px] py-5" style={{
                  borderColor: 'rgba(17,24,39,.06)',
                  boxShadow: '0 10px 30px rgba(17,24,39,.08)'
                }}>
                  <div className="absolute -left-[8px] top-[22px] w-4 h-4 bg-white border-l border-b rotate-45 rounded-bl" style={{
                    borderColor: 'rgba(17,24,39,.06)'
                  }}></div>
                  <p className="text-[26px] font-extrabold tracking-tight m-0 mb-3 text-gray-900">Noor AI</p>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                    <span className="text-[17px] text-gray-700 italic">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* AI Usage Bubble - Floating above footer on right */}
      {selectedMode && usageData && (
        <div 
          className="fixed z-40"
          style={{
            bottom: '85px',  // Adjust this value to move bubble up/down
            right: '24px',   // Adjust this value to move bubble left/right
          }}
        >
          <div className="ai-usage-bubble" role="status" aria-label="AI Questions usage">
            <div className="ai-usage-row">
              <div className="ai-usage-title">AI Questions</div>
              <div className="ai-usage-count">
                <span className="ai-usage-used">{usageData.questions_used_this_month}</span>
                <span className="ai-usage-sep">/</span>
                <span className="ai-usage-total">{usageData.limit}</span>
              </div>
            </div>

            <div className="ai-usage-bar" aria-hidden="true">
              <div className="ai-usage-bar-fill" style={{ width: `${usageData.percentage_used}%` }}></div>
            </div>

            <div className="ai-usage-footer">
              <span className="ai-usage-reset">Resets {new Date(usageData.usage_reset_date).toLocaleDateString('en-GB').replace(/\//g, '/')}</span>
              <span className="ai-usage-dot" aria-hidden="true"></span>
            </div>
          </div>
        </div>
      )}

      {/* Footer - Fixed Input Bar */}
      {selectedMode && (
        <footer className="flex-shrink-0 bg-white/65 border-t backdrop-blur-[10px] p-[14px]" style={{
          borderColor: 'rgba(17,24,39,.06)',
          boxShadow: '0 -4px 20px rgba(17, 24, 39, 0.05)'
        }}>
          {/* Image Previews */}
          {uploadedImages.length > 0 && (
            <div className="mb-3 flex gap-2 flex-wrap">
              {uploadedImages.map((img, index) => (
                <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200">
                  <img src={img} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-bl-lg flex items-center justify-center hover:bg-red-600 transition-colors"
                    aria-label="Remove image"
                  >
                    <X size={12} className="text-white" />
                  </button>
                </div>
              ))}
              <div className="text-xs text-gray-500 self-center ml-2">{uploadedImages.length}/5 images</div>
            </div>
          )}

          <div className="flex items-end gap-3">
            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              onFocus={(e) => {
                // Hide scrollbar on focus (when typing)
                e.currentTarget.style.overflowY = 'hidden';
              }}
              onBlur={(e) => {
                // Show scrollbar when not focused
                if (e.currentTarget.scrollHeight > e.currentTarget.clientHeight) {
                  e.currentTarget.style.overflowY = 'auto';
                }
              }}
              onWheel={(e) => {
                // Show scrollbar when scrolling
                const target = e.currentTarget;
                target.style.overflowY = 'auto';
                // Hide after scroll ends
                setTimeout(() => {
                  if (document.activeElement !== target) {
                    target.style.overflowY = 'hidden';
                  }
                }, 1000);
              }}
              placeholder="Ask about your property assessment..."
              rows={1}
              className="flex-1 min-h-[56px] max-h-[120px] rounded-3xl border bg-white/92 px-[18px] py-4 text-[17px] outline-none text-gray-900 placeholder-gray-900/35 resize-none overflow-y-hidden" style={{
                borderColor: 'rgba(17,24,39,.08)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,.7)'
              }}
            />
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
            />
            
            {/* Image upload button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadedImages.length >= 5 || isLoading}
              className={`w-[58px] h-[58px] rounded-full border-none flex items-center justify-center flex-shrink-0 transition-all ${
                uploadedImages.length < 5 && !isLoading
                  ? 'bg-gray-100 hover:bg-gray-200 cursor-pointer'
                  : 'bg-gray-100 cursor-not-allowed opacity-40'
              }`}
              aria-label="Upload images"
            >
              <ImageIcon size={24} className="text-gray-600" />
            </button>

            <button
              onClick={handleSendMessage}
              disabled={((!inputText.trim() && uploadedImages.length === 0) || isLoading || !selectedMode)}
              className={`w-[58px] h-[58px] rounded-full border-none flex items-center justify-center flex-shrink-0 transition-transform active:translate-y-px ${
                (inputText.trim() || uploadedImages.length > 0) && !isLoading && selectedMode
                  ? 'bg-[#f7c948] cursor-pointer'
                  : 'bg-gray-300 cursor-not-allowed opacity-50'
              }`}
              style={{
                boxShadow: (inputText.trim() || uploadedImages.length > 0) && !isLoading ? '0 14px 30px rgba(247, 201, 72, .35)' : 'none'
              }}
              aria-label="Send"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-3 border-gray-900/90 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send size={24} className="text-gray-900/90" />
              )}
            </button>
          </div>
        </footer>
      )}
      </div>

      {/* Image Viewer Modal */}
      {viewingImage && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setViewingImage(null)}
        >
          <button
            onClick={() => setViewingImage(null)}
            className="absolute w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            style={{
              top: 'max(1rem, env(safe-area-inset-top))',
              right: 'max(1rem, env(safe-area-inset-right))'
            }}
            aria-label="Close"
          >
            <X size={24} className="text-white" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSaveImage(viewingImage);
            }}
            className="absolute px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 flex items-center gap-2 transition-colors text-white font-medium"
            style={{
              top: 'max(1rem, env(safe-area-inset-top))',
              left: 'max(1rem, env(safe-area-inset-left))'
            }}
            aria-label="Save image"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Save
          </button>
          
          <img 
            src={viewingImage} 
            alt="Full size view"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
    </>
  );
}

export default ChatPage;